import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Box,
} from '@mui/material';
import { Industry } from '../../../models/Industry';
import { addIndustry } from '../../../services/industryService';

interface EditCatalogComponentProps {
    open: boolean;
    onClose: () => void;
    industry: Industry | null;
}

const EditCatalogComponent: React.FC<EditCatalogComponentProps> = ({
    open,
    onClose,
    industry
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState<Omit<Industry, "id">>({
        name: '',
        status: 'pending',
        isActive: true,
        complexity: 'low',
        keywords: [],
        description: '',
        commonIssues: [],
        statistics: {
            averageRevenue: 0,
            marketSize: 0,
            growthRate: 0,
            employeeCount: 0
        },
        risks: [],
        opportunities: [],
        averageRevenue: 0,
        severity: 'low',
        createdAt: new Date(),
        updatedAt: new Date()
    });

    const handleChange = (field: keyof Industry, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleKeywordsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const keywords = event.target.value.split(',').map(k => k.trim());
        handleChange('keywords', keywords);
    };

    const handleSubmit = async () => {
        try {
            setIsLoading(true);
            await addIndustry(formData);
            onClose();
            // Reset form
            setFormData({
                name: '',
                status: 'pending',
                isActive: true,
                complexity: 'low',
                keywords: [],
                description: '',
                commonIssues: [],
                statistics: {
                    averageRevenue: 0,
                    marketSize: 0,
                    growthRate: 0,
                    employeeCount: 0
                },
                risks: [],
                opportunities: [],
                averageRevenue: 0,
                severity: 'low',
                createdAt: new Date(),
                updatedAt: new Date()
            });
        } catch (error) {
            console.error('Error adding industry:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            className="modern-modal"
        >
            <DialogTitle sx={{
                borderBottom: '1px solid var(--neutral-200)',
                backgroundColor: 'var(--neutral-50)',
                py: 2
            }}>
                Add New Industry
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <TextField
                        label="Name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        fullWidth
                        required
                    />

                    <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={formData.status}
                            label="Status"
                            onChange={(e) => handleChange('status', e.target.value)}
                        >
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="published">Published</MenuItem>
                            <MenuItem value="archived">Archived</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Keywords (comma-separated)"
                        value={formData.keywords?.join(', ')}
                        onChange={handleKeywordsChange}
                        fullWidth
                        helperText="Enter keywords separated by commas"
                    />

                    <TextField
                        label="Description"
                        value={formData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        fullWidth
                        multiline
                        rows={4}
                    />

                    {formData.keywords && formData.keywords.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {formData.keywords.map((keyword, index) => (
                                <Chip
                                    key={index}
                                    label={keyword}
                                    onDelete={() => {
                                        const newKeywords = formData.keywords?.filter((_, i) => i !== index);
                                        handleChange('keywords', newKeywords);
                                    }}
                                    className="status-chip"
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{
                borderTop: '1px solid var(--neutral-200)',
                backgroundColor: 'var(--neutral-50)',
                p: 2
            }}>
                <Button
                    onClick={onClose}
                    disabled={isLoading}
                    className="modern-button"
                    sx={{ color: 'var(--primary-600)' }}
                >
                    CANCEL
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!formData.name || isLoading}
                    sx={{
                        backgroundColor: '#1976d2',
                        color: '#fff',
                        '&:hover': {
                            backgroundColor: '#1565c0'
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#E0E0E0',
                            color: 'rgba(0, 0, 0, 0.38)'
                        }
                    }}
                >
                    {isLoading ? 'SAVING...' : 'ADD INDUSTRY'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditCatalogComponent; 